import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { API_BASE_URL } from '@app/core/tokens/api-base-url.token';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { hasBackendMessage } from '@app/core/utils/backend-error';
import {
  CreateSubcategoryDictionaryRequest,
  SubcategoryDictionaryModel,
  SubcategoryDictionarySearchRequest,
  SubcategoryDictionarySearchResponse,
} from '@app/core/models/product-parameters/subcategory-dictionary.model';

const EMPTY_SUBCATEGORY_DICTIONARIES_PAGE: SubcategoryDictionarySearchResponse = {
  currentPageNumber: 0,
  pageSize: 10,
  totalElements: 0,
  totalPages: 0,
  isLastPage: true,
  isFirstPage: true,
  isEmpty: true,
  content: [],
};

@Injectable({
  providedIn: 'root',
})
export class SubcategoryDictionaryService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);

  readonly isLoading = signal(false);

  private get baseUrl(): string {
    return `${this.apiBaseUrl}/b2b/subcategory-dictionaries`;
  }

  search(
    page = 0,
    size = 10,
    body: SubcategoryDictionarySearchRequest = {}
  ): Observable<SubcategoryDictionarySearchResponse> {
    this.isLoading.set(true);
    const params = new HttpParams().set('page', page.toString()).set('size', size.toString());

    return this.http
      .post<SubcategoryDictionarySearchResponse>(`${this.baseUrl}/search`, body, { params })
      .pipe(
        catchError(error => {
          console.error('Ошибка при получении справочника подкатегорий:', error);
          this.translate.get('Ошибка при получении подкатегорий').subscribe(msg => {
            this.snackbarService.error(msg);
          });
          return of({ ...EMPTY_SUBCATEGORY_DICTIONARIES_PAGE, pageSize: size });
        }),
        finalize(() => this.isLoading.set(false))
      );
  }

  create(body: CreateSubcategoryDictionaryRequest): Observable<SubcategoryDictionaryModel> {
    this.isLoading.set(true);
    return this.http.post<SubcategoryDictionaryModel>(this.baseUrl, body).pipe(
      catchError(error => {
        console.error('Ошибка при создании подкатегории:', error);
        // Текст бэкенда (дубль названия и т.п.) показывает диалог — без второго общего тоста
        if (!hasBackendMessage(error)) {
          this.translate.get('Ошибка при создании подкатегории').subscribe(msg => {
            this.snackbarService.error(msg);
          });
        }
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  update(
    dictionaryId: string,
    body: CreateSubcategoryDictionaryRequest
  ): Observable<SubcategoryDictionaryModel> {
    this.isLoading.set(true);
    return this.http.put<SubcategoryDictionaryModel>(`${this.baseUrl}/${dictionaryId}`, body).pipe(
      catchError(error => {
        console.error('Ошибка при обновлении подкатегории:', error);
        if (!hasBackendMessage(error)) {
          this.translate.get('Ошибка при обновлении подкатегории').subscribe(msg => {
            this.snackbarService.error(msg);
          });
        }
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  delete(dictionaryId: string): Observable<void> {
    this.isLoading.set(true);
    return this.http.delete<void>(`${this.baseUrl}/${dictionaryId}`).pipe(
      catchError(error => {
        console.error('Ошибка при удалении подкатегории:', error);
        this.translate.get('Ошибка при удалении подкатегории').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  activate(dictionaryId: string): Observable<SubcategoryDictionaryModel> {
    this.isLoading.set(true);
    return this.http
      .patch<SubcategoryDictionaryModel>(`${this.baseUrl}/${dictionaryId}/activate`, {})
      .pipe(
        catchError(error => {
          console.error('Ошибка при активации подкатегории:', error);
          this.translate.get('Ошибка при активации подкатегории').subscribe(msg => {
            this.snackbarService.error(msg);
          });
          throw error;
        }),
        finalize(() => this.isLoading.set(false))
      );
  }

  deactivate(dictionaryId: string): Observable<SubcategoryDictionaryModel> {
    this.isLoading.set(true);
    return this.http
      .patch<SubcategoryDictionaryModel>(`${this.baseUrl}/${dictionaryId}/deactivate`, {})
      .pipe(
        catchError(error => {
          console.error('Ошибка при деактивации подкатегории:', error);
          this.translate.get('Ошибка при деактивации подкатегории').subscribe(msg => {
            this.snackbarService.error(msg);
          });
          throw error;
        }),
        finalize(() => this.isLoading.set(false))
      );
  }
}
